import { OnboardingStatus, OnboardingStatusValues } from '../value-objects/OnboardingStatus';

/**
 * OnboardingProgress Entity Properties
 */
export interface OnboardingProgressProps {
  id: string;
  userId: string;
  role: 'director' | 'teacher' | 'student';
  status: OnboardingStatus;
  currentStepIndex: number;
  totalSteps: number;
  startedAt: Date | null;
  completedAt: Date | null;
  skippedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * OnboardingProgress Entity
 * Represents the current state of a user's onboarding journey
 *
 * DB Table: onboarding_progress
 * - id (PK)
 * - user_id (FK -> auth.users)
 * - role (ENUM: director, teacher, student)
 * - status (ENUM: pending, in_progress, completed, skipped)
 * - current_step_index
 * - started_at
 * - completed_at
 * - skipped_at
 * - created_at
 * - updated_at
 */
export class OnboardingProgress {
  private readonly props: OnboardingProgressProps;

  constructor(props: OnboardingProgressProps) {
    this.props = props;
  }

  get id(): string {
    return this.props.id;
  }

  get userId(): string {
    return this.props.userId;
  }

  get role(): 'director' | 'teacher' | 'student' {
    return this.props.role;
  }

  get status(): OnboardingStatus {
    return this.props.status;
  }

  get currentStepIndex(): number {
    return this.props.currentStepIndex;
  }

  get totalSteps(): number {
    return this.props.totalSteps;
  }

  get startedAt(): Date | null {
    return this.props.startedAt;
  }

  get completedAt(): Date | null {
    return this.props.completedAt;
  }

  get skippedAt(): Date | null {
    return this.props.skippedAt;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get updatedAt(): Date {
    return this.props.updatedAt;
  }

  /**
   * Check if onboarding is pending (not started)
   */
  isPending(): boolean {
    return this.props.status === OnboardingStatusValues.PENDING;
  }

  /**
   * Check if onboarding is in progress
   */
  isInProgress(): boolean {
    return this.props.status === OnboardingStatusValues.IN_PROGRESS;
  }

  /**
   * Check if onboarding is completed
   */
  isCompleted(): boolean {
    return this.props.status === OnboardingStatusValues.COMPLETED;
  }

  /**
   * Check if onboarding was skipped
   */
  isSkipped(): boolean {
    return this.props.status === OnboardingStatusValues.SKIPPED;
  }

  /**
   * Check if onboarding should be shown to user
   * True if status is pending or in_progress
   */
  shouldShowOnboarding(): boolean {
    return this.isPending() || this.isInProgress();
  }

  /**
   * Check if onboarding has finished (completed or skipped)
   */
  hasFinished(): boolean {
    return this.isCompleted() || this.isSkipped();
  }

  /**
   * Get progress as a percentage (0-100)
   */
  getProgressPercentage(): number {
    if (this.props.totalSteps === 0) return 0;
    return Math.round((this.props.currentStepIndex / this.props.totalSteps) * 100);
  }

  /**
   * Check if user is on the first step
   */
  isFirstStep(): boolean {
    return this.props.currentStepIndex === 0;
  }

  /**
   * Check if user is on the last step
   */
  isLastStep(): boolean {
    return this.props.currentStepIndex === this.props.totalSteps - 1;
  }

  /**
   * Get number of remaining steps
   */
  getRemainingSteps(): number {
    return Math.max(0, this.props.totalSteps - this.props.currentStepIndex);
  }

  /**
   * Create a new instance with incremented step index
   * Automatically marks as completed if reaching the end
   */
  withNextStep(): OnboardingProgress {
    const nextIndex = this.props.currentStepIndex + 1;
    const reachedEnd = nextIndex >= this.props.totalSteps;

    return new OnboardingProgress({
      ...this.props,
      currentStepIndex: nextIndex,
      status: reachedEnd ? OnboardingStatusValues.COMPLETED : this.props.status,
      completedAt: reachedEnd ? new Date() : this.props.completedAt,
      updatedAt: new Date(),
    });
  }

  /**
   * Create a new instance with decremented step index
   * Does not go below 0
   */
  withPreviousStep(): OnboardingProgress {
    const previousIndex = Math.max(0, this.props.currentStepIndex - 1);

    return new OnboardingProgress({
      ...this.props,
      currentStepIndex: previousIndex,
      updatedAt: new Date(),
    });
  }

  /**
   * Create a new instance marked as started (in_progress)
   */
  withStarted(): OnboardingProgress {
    return new OnboardingProgress({
      ...this.props,
      status: OnboardingStatusValues.IN_PROGRESS,
      startedAt: this.props.startedAt ?? new Date(),
      updatedAt: new Date(),
    });
  }

  /**
   * Create a new instance marked as completed
   */
  withCompleted(): OnboardingProgress {
    return new OnboardingProgress({
      ...this.props,
      status: OnboardingStatusValues.COMPLETED,
      completedAt: new Date(),
      updatedAt: new Date(),
    });
  }

  /**
   * Create a new instance marked as skipped
   */
  withSkipped(): OnboardingProgress {
    return new OnboardingProgress({
      ...this.props,
      status: OnboardingStatusValues.SKIPPED,
      skippedAt: new Date(),
      updatedAt: new Date(),
    });
  }

  /**
   * Create a new instance reset to initial state
   */
  withReset(): OnboardingProgress {
    return new OnboardingProgress({
      ...this.props,
      status: OnboardingStatusValues.PENDING,
      currentStepIndex: 0,
      startedAt: null,
      completedAt: null,
      skippedAt: null,
      updatedAt: new Date(),
    });
  }

  /**
   * Convert to plain object for serialization
   */
  toPlainObject(): OnboardingProgressProps {
    return { ...this.props };
  }
}
